import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-border"}`} />
    ))}
  </div>
);

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, message: "", order_number: "" });

  const loadReviews = () => {
    api.get("/reviews").then((res) => setReviews(res.data)).catch(() => setReviews([]));
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", {
        name: form.name,
        location: form.location || null,
        rating: form.rating,
        message: form.message,
        order_number: form.order_number || null,
      });
      track("review_submitted", { rating: form.rating });
      toast.success(data.message || "Thank you for your review!");
      setForm({ name: "", location: "", rating: 5, message: "", order_number: "" });
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not submit your review."));
    } finally {
      setSubmitting(false);
    }
  };

  const ReviewForm = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="leave-review-btn"
          onClick={() => track("leave_review_click")}
          className="rounded-sm bg-foreground font-semibold text-background hover:bg-foreground/90"
        >
          Leave a review
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Share your experience</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Your rating</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`rating-star-${n}`}
                  onClick={() => setForm((f) => ({ ...f, rating: n }))}
                  className="p-0.5"
                  aria-label={`${n} star`}
                >
                  <Star className={`h-7 w-7 transition-colors ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-border hover:text-amber-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rv-name">Name</Label>
              <Input id="rv-name" data-testid="review-name" className="rounded-sm" value={form.name} onChange={set("name")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rv-loc">Location <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="rv-loc" data-testid="review-location" placeholder="e.g. Lagos" className="rounded-sm" value={form.location} onChange={set("location")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rv-msg">Your review</Label>
            <Textarea id="rv-msg" data-testid="review-message" rows={4} className="rounded-sm" value={form.message} onChange={set("message")} placeholder="Tell others about your experience with VisaFlight." />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rv-order">Order number <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="rv-order" data-testid="review-order" placeholder="VF-260001" className="rounded-sm" value={form.order_number} onChange={set("order_number")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="submit-review-btn" onClick={submit} disabled={submitting} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#003DB3]">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <section id="reviews" className="border-b border-border py-20 lg:py-24">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">What our customers say</h2>
            <p className="mt-3 max-w-xl text-muted-foreground">Real experiences from applicants who used VisaFlight for their visa documentation.</p>
          </div>
          {ReviewForm}
        </div>

        {reviews.length === 0 ? (
          <div className="mt-12 rounded-sm border border-dashed border-border p-10 text-center">
            <Quote className="mx-auto h-8 w-8 text-border" />
            <p className="mt-4 text-muted-foreground">No reviews yet — be the first to share your experience.</p>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                className="flex flex-col rounded-sm border border-border bg-card p-6"
                data-testid="review-card"
              >
                <Stars value={r.rating} />
                <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground">“{r.message}”</p>
                <div className="mt-5 border-t border-border pt-4">
                  <p className="font-heading text-sm font-semibold text-foreground">{r.name}</p>
                  {r.location && <p className="text-xs text-muted-foreground">{r.location}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
